'use client'

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { WagamourV2Modal } from '@/components/v2/WagamourV2Modal'
import { WagamourV2Button } from '@/components/v2/WagamourV2Button'
import { WagamourV2ModalEyebrow } from '@/components/v2/WagamourV2ModalContent'
import { PlayCircleOrbitAvatar } from '@/components/play/PlayCircleOrbitAvatar'
import type { PendingInboundItem } from '@/lib/play/playCirclePendingReadService'
import {
  canActOnConnectAccept,
  formatConnectAcceptedMessage,
  resolveConnectAcceptSelection,
} from '@/lib/play/connectAcceptanceClient'
import { CONNECT_HANDOFF_COMPLETION_COPY } from '@/lib/play/connectHandoffCompletionCopy'
import { v2ModalStyles } from '@/lib/design/v2ModalContent'
import { playV2FocusRing } from '@/lib/play/playV2Focus'

export type IncomingConnectionReviewSheetProps = {
  isOpen: boolean
  onClose: () => void
  inbound: PendingInboundItem | null
  /** Active Play dog — preferred when eligible. */
  preferredRecipientDogId: string
  recipientDogFallback: { name: string; photoUrl: string | null }
  onAccept: (
    connectionRef: string,
    recipientDogId: string
  ) => Promise<{ ok: true; friendName: string } | { ok: false; message: string }>
  onDecline: (
    connectionRef: string,
    recipientDogId: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  /** After accept — open the new relationship in Play. */
  onViewRelationship?: (friendDogId: string) => void
  portalContainer?: HTMLElement | null
  returnFocusRef?: React.RefObject<HTMLElement | null>
}

type SheetView = 'review' | 'accept-confirm' | 'decline-confirm' | 'accepted'

function InboundDogCardPanel({
  inbound,
}: {
  inbound: PendingInboundItem
}) {
  const { initiatorDog, guardian, metAtParkName } = inbound
  const contextLine = `${initiatorDog.breed} · ${guardian.firstName}`

  return (
    <div
      className="rounded-[20px] border p-4"
      style={v2ModalStyles.nestedCard}
      data-incoming-connection-dog-card="true"
    >
      <p
        className="text-[10px] font-semibold uppercase tracking-[0.14em]"
        style={v2ModalStyles.eyebrow}
      >
        Connection request
      </p>
      <h3 className="mt-1 text-lg font-bold" style={v2ModalStyles.headline}>
        {initiatorDog.name}
      </h3>
      <p className="text-sm" style={v2ModalStyles.body}>
        {contextLine}
      </p>

      <div className="mt-4 flex justify-center">
        <PlayCircleOrbitAvatar
          name={initiatorDog.name}
          photoUrl={initiatorDog.photoUrl}
          size={96}
          variant="friend"
        />
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt
            className="text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={v2ModalStyles.eyebrow}
          >
            Size
          </dt>
          <dd className="font-medium capitalize" style={v2ModalStyles.primary}>
            {initiatorDog.size}
          </dd>
        </div>
        {initiatorDog.playPersonality ? (
          <div>
            <dt
              className="text-[10px] font-semibold uppercase tracking-[0.12em]"
              style={v2ModalStyles.eyebrow}
            >
              Play style
            </dt>
            <dd className="font-medium" style={v2ModalStyles.primary}>
              {initiatorDog.playPersonality}
            </dd>
          </div>
        ) : null}
      </dl>

      {metAtParkName ? (
        <p className="mt-3 text-xs" style={v2ModalStyles.metadata}>
          Met at <span className="font-medium">{metAtParkName}</span>
        </p>
      ) : null}
    </div>
  )
}

export function IncomingConnectionReviewSheet({
  isOpen,
  onClose,
  inbound,
  preferredRecipientDogId,
  recipientDogFallback,
  onAccept,
  onDecline,
  onViewRelationship,
  portalContainer = null,
  returnFocusRef,
}: IncomingConnectionReviewSheetProps) {
  const titleId = useId()
  const statusId = useId()
  const [view, setView] = useState<SheetView>('review')
  const [actionBusy, setActionBusy] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [inboundSnapshot, setInboundSnapshot] = useState<PendingInboundItem | null>(null)
  const [selectedDogId, setSelectedDogId] = useState<string | null>(null)
  const submitLockRef = useRef(false)
  const openedRef = useRef(false)

  const selection = useMemo(() => {
    if (!inboundSnapshot) {
      return resolveConnectAcceptSelection({ eligibleDogs: [] })
    }
    return resolveConnectAcceptSelection({
      eligibleDogs: inboundSnapshot.eligibleDogs ?? [],
      preferredDogId: preferredRecipientDogId,
      blockReason: inboundSnapshot.blockReason,
      availableActions: inboundSnapshot.availableActions,
      senderDogName: inboundSnapshot.initiatorDog.name,
    })
  }, [inboundSnapshot, preferredRecipientDogId])

  const canAct = useMemo(
    () =>
      canActOnConnectAccept(
        selection,
        selectedDogId,
        inboundSnapshot?.availableActions ?? ['ACCEPT', 'DECLINE']
      ),
    [inboundSnapshot?.availableActions, selectedDogId, selection]
  )

  const selectedDog = useMemo(() => {
    if (!selectedDogId) return null
    return (
      selection.selectableDogs.find((dog) => dog.id === selectedDogId) ?? null
    )
  }, [selectedDogId, selection.selectableDogs])

  const resetSheet = useCallback(() => {
    setView('review')
    setActionBusy(false)
    setStatusMessage(null)
    setSelectedDogId(null)
    submitLockRef.current = false
  }, [])

  const handleClose = useCallback(() => {
    resetSheet()
    onClose()
    window.requestAnimationFrame(() => {
      returnFocusRef?.current?.focus()
    })
  }, [onClose, resetSheet, returnFocusRef])

  useEffect(() => {
    if (isOpen && inbound) {
      setInboundSnapshot(inbound)
      const next = resolveConnectAcceptSelection({
        eligibleDogs: inbound.eligibleDogs ?? [],
        preferredDogId: preferredRecipientDogId,
        blockReason: inbound.blockReason,
        availableActions: inbound.availableActions,
        senderDogName: inbound.initiatorDog.name,
      })
      setSelectedDogId(next.initialDogId)
    }
    if (!isOpen) {
      setInboundSnapshot(null)
    }
  }, [isOpen, inbound, preferredRecipientDogId])

  useEffect(() => {
    if (isOpen) {
      openedRef.current = true
      return
    }
    if (openedRef.current) {
      resetSheet()
    }
  }, [isOpen, resetSheet])

  const runAccept = useCallback(async () => {
    if (!inboundSnapshot || !selectedDogId || !canAct || actionBusy || submitLockRef.current) {
      return
    }
    submitLockRef.current = true
    setActionBusy(true)
    setStatusMessage(null)
    try {
      const result = await onAccept(inboundSnapshot.connectionRef, selectedDogId)
      if (!result.ok) {
        setStatusMessage(result.message)
        submitLockRef.current = false
        return
      }
      const recipientName =
        selectedDog?.name?.trim() ||
        recipientDogFallback.name.trim() ||
        'your dog'
      setView('accepted')
      setStatusMessage(
        formatConnectAcceptedMessage(result.friendName, recipientName)
      )
    } finally {
      setActionBusy(false)
    }
  }, [
    actionBusy,
    canAct,
    inboundSnapshot,
    onAccept,
    recipientDogFallback.name,
    selectedDog?.name,
    selectedDogId,
  ])

  const runDecline = useCallback(async () => {
    if (!inboundSnapshot || !selectedDogId || actionBusy || submitLockRef.current) return
    submitLockRef.current = true
    setActionBusy(true)
    setStatusMessage(null)
    try {
      const result = await onDecline(inboundSnapshot.connectionRef, selectedDogId)
      if (!result.ok) {
        setStatusMessage(result.message)
        submitLockRef.current = false
        return
      }
      handleClose()
    } finally {
      setActionBusy(false)
    }
  }, [actionBusy, handleClose, inboundSnapshot, onDecline, selectedDogId])

  if (!inboundSnapshot) return null

  const friendName = inboundSnapshot.initiatorDog.name.trim() || 'Their dog'
  const recipientName =
    selectedDog?.name?.trim() ||
    recipientDogFallback.name.trim() ||
    'your dog'
  const isBlocked = selection.blockReason != null

  return (
    <WagamourV2Modal
      isOpen={isOpen}
      onClose={handleClose}
      presentation="sheet"
      portalContainer={portalContainer}
      preventClose={actionBusy}
      ariaLabelledBy={titleId}
      header={
        <div className="space-y-1">
          {view === 'decline-confirm' || view === 'accept-confirm' ? (
            <button
              type="button"
              className={`mb-1 inline-flex min-h-[44px] items-center gap-1 text-sm text-white/75 hover:text-white ${playV2FocusRing}`}
              disabled={actionBusy}
              onClick={() => setView('review')}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back
            </button>
          ) : null}
          <WagamourV2ModalEyebrow>Connection</WagamourV2ModalEyebrow>
          <h2 id={titleId} className="text-base font-bold leading-snug" style={v2ModalStyles.headline}>
            {view === 'decline-confirm'
              ? 'Decline connection?'
              : view === 'accept-confirm'
                ? 'Accept connection?'
                : view === 'accepted'
                  ? CONNECT_HANDOFF_COMPLETION_COPY.CONNECTED.title
                  : 'Review connection'}
          </h2>
        </div>
      }
      footer={
        view === 'review' ? (
          isBlocked ? (
            <WagamourV2Button type="button" fullWidth onClick={handleClose}>
              Close
            </WagamourV2Button>
          ) : (
            <div className="space-y-2">
              <WagamourV2Button
                type="button"
                fullWidth
                disabled={actionBusy || !canAct}
                onClick={() => setView('accept-confirm')}
                data-incoming-connection-accept="true"
              >
                Accept
              </WagamourV2Button>
              <WagamourV2Button
                type="button"
                fullWidth
                variant="secondary"
                disabled={actionBusy || !selectedDogId}
                onClick={() => setView('decline-confirm')}
                data-incoming-connection-decline="true"
              >
                Decline
              </WagamourV2Button>
            </div>
          )
        ) : view === 'accept-confirm' ? (
          <div className="space-y-2">
            <WagamourV2Button
              type="button"
              fullWidth
              disabled={actionBusy || !canAct}
              onClick={() => void runAccept()}
              data-incoming-connection-accept-confirm="true"
            >
              {actionBusy ? 'Accepting…' : 'Yes, accept'}
            </WagamourV2Button>
            <WagamourV2Button
              type="button"
              fullWidth
              variant="secondary"
              disabled={actionBusy}
              onClick={() => setView('review')}
            >
              Keep reviewing
            </WagamourV2Button>
          </div>
        ) : view === 'decline-confirm' ? (
          <div className="space-y-2">
            <WagamourV2Button
              type="button"
              fullWidth
              variant="secondary"
              disabled={actionBusy || !selectedDogId}
              onClick={() => void runDecline()}
              data-incoming-connection-decline-confirm="true"
            >
              {actionBusy ? 'Declining…' : 'Yes, decline'}
            </WagamourV2Button>
            <WagamourV2Button
              type="button"
              fullWidth
              disabled={actionBusy}
              onClick={() => setView('review')}
            >
              Keep reviewing
            </WagamourV2Button>
          </div>
        ) : (
          <WagamourV2Button
            type="button"
            fullWidth
            onClick={() => {
              const friendId = inboundSnapshot?.initiatorDog.id
              if (friendId && onViewRelationship) {
                onViewRelationship(friendId)
              }
              handleClose()
            }}
            data-incoming-connection-view-relationship="true"
          >
            View relationship
          </WagamourV2Button>
        )
      }
      contentClassName="pb-[max(0.5rem,env(safe-area-inset-bottom,0px))]"
    >
      <div
        className="space-y-4"
        aria-labelledby={titleId}
        data-incoming-connection-review-sheet="true"
        data-incoming-connection-view={view}
      >
        {view === 'accept-confirm' ? (
          <p className="text-sm leading-relaxed" style={v2ModalStyles.body}>
            {friendName} will join {recipientName}&apos;s Play Circle. Either of you can plan
            playdates after you connect.
          </p>
        ) : null}
        {view === 'review' || view === 'accepted' ? (
          <>
            <div className="flex items-center gap-3 rounded-[20px] border border-white/10 bg-white/5 p-3">
              <PlayCircleOrbitAvatar
                name={recipientName}
                photoUrl={selectedDog?.photoUrl ?? recipientDogFallback.photoUrl}
                size={48}
                variant="center"
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-white/60">For</p>
                <p className="text-sm font-semibold text-white">{recipientName}</p>
              </div>
            </div>

            <InboundDogCardPanel inbound={inboundSnapshot} />

            {isBlocked ? (
              <p
                className="text-sm leading-relaxed text-amber-100/95"
                data-incoming-connection-block="true"
                data-incoming-connection-block-reason={selection.blockReason ?? undefined}
              >
                {selection.blockMessage}
              </p>
            ) : (
              <p className="text-sm leading-relaxed" style={v2ModalStyles.body}>
                {friendName}&apos;s parent wants to connect with {recipientName}. Accepting adds{' '}
                {friendName} to your Play Circle.
              </p>
            )}

            {!isBlocked && selection.requiresDogSelection ? (
              <div
                className="space-y-2 rounded-[16px] border border-white/10 bg-white/5 p-3"
                data-incoming-connection-dog-select="true"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-white/55">
                  Which dog is connecting?
                </p>
                <div className="space-y-2">
                  {selection.selectableDogs.map((dog) => (
                    <label
                      key={dog.id}
                      className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-2xl border px-3 py-2 ${
                        selectedDogId === dog.id
                          ? 'border-amber-300/60 bg-amber-50/10'
                          : 'border-white/10 bg-white/5'
                      }`}
                    >
                      <input
                        type="radio"
                        name="incoming-recipient-dog"
                        value={dog.id}
                        checked={selectedDogId === dog.id}
                        onChange={() => setSelectedDogId(dog.id)}
                        disabled={actionBusy}
                      />
                      <PlayCircleOrbitAvatar
                        name={dog.name}
                        photoUrl={dog.photoUrl}
                        size={36}
                        variant="friend"
                      />
                      <span className="text-sm font-medium text-white">{dog.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-sm leading-relaxed" style={v2ModalStyles.body}>
            {friendName} won&apos;t be added to {recipientName}&apos;s Play Circle. The other parent
            won&apos;t receive a decline notification.
          </p>
        )}

        <p
          id={statusId}
          className="min-h-[1.25rem] text-center text-xs text-emerald-200/90"
          role="status"
          aria-live="polite"
          data-incoming-connection-status="true"
        >
          {statusMessage}
        </p>
      </div>
    </WagamourV2Modal>
  )
}
